import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import axios from 'axios';
import { activateTrial } from '../cabinet.api';
import { CabinetView, DashboardState } from '../cabinet.schemas';
import { serverTiles } from '../cabinet.mock';
import { createServerTilesWithPing, getDeviceSlotsText, getDevicesLimit, getFreeDeviceSlots } from '../cabinet.utils';
import { openExternalLink, TELEGRAM_LINKS } from '../../../shared/links';

type DashboardPageProps = {
  dashboardState: DashboardState;
  isLoading: boolean;
  canConnectTelegram: boolean;
  isTelegramLinked: boolean;
  isTelegramConnecting: boolean;
  telegramStatus: string;
  telegramUsername: string;
  onConnectTelegram: () => void;
  onRefreshDashboard: () => void;
  setDashboardState: Dispatch<SetStateAction<DashboardState>>;
  onSelectView: (view: CabinetView) => void;
};

function getSubscriptionTitle(dashboardState: DashboardState) {
  const subInfo = dashboardState.subInfo;

  if (!subInfo?.is_active) {
    return 'Подписка не активна  😢';
  }

  const title = subInfo.sub_title || subInfo.title || subInfo.name || 'Подписка активна';
  return title;
}

function formatSubscriptionEndDate(endDate?: string) {
  if (!endDate) {
    return '';
  }

  const date = new Date(endDate);

  if (Number.isNaN(date.getTime())) {
    return endDate;
  }

  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function getDaysUntilEnd(endDate?: string) {
  if (!endDate) {
    return null;
  }

  const date = new Date(endDate);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const dayMs = 24 * 60 * 60 * 1000;
  return Math.ceil((date.getTime() - Date.now()) / dayMs);
}

function getTrialActivationError(error: unknown) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;

    if (typeof data === 'string') {
      return data;
    }

    if (data && typeof data === 'object') {
      if (
        'code' in data
        && data.code === 'telegram_required'
        && 'instruction' in data
        && typeof data.instruction === 'string'
      ) {
        return data.instruction;
      }

      const message =
        'detail' in data && typeof data.detail === 'string'
          ? data.detail
          : 'message' in data && typeof data.message === 'string'
            ? data.message
            : null;

      if (message) {
        return message;
      }
    }
  }

  return 'Не удалось активировать пробную подписку. Попробуй еще раз.';
}

function getTrialStatusLabel(status?: string, canActivate?: boolean, telegramLinked?: boolean) {
  if (canActivate) {
    return 'Можно активировать';
  }

  if (status === 'telegram_required' || !telegramLinked) {
    return 'Нужно привязать Telegram';
  }

  if (status === 'already_used') {
    return 'Уже использована';
  }

  if (status === 'active') {
    return 'Активна';
  }

  return 'Проверяем доступность';
}

export function DashboardPage({
  dashboardState,
  isLoading,
  canConnectTelegram,
  isTelegramLinked,
  isTelegramConnecting,
  telegramStatus,
  telegramUsername,
  onConnectTelegram,
  onRefreshDashboard,
  setDashboardState,
  onSelectView,
}: DashboardPageProps) {
  const [trialError, setTrialError] = useState('');
  const [isTrialActivating, setIsTrialActivating] = useState(false);
  const [isTrialSuccessOpen, setIsTrialSuccessOpen] = useState(false);
  const hasActiveSubscription = Boolean(dashboardState.subInfo?.is_active);
  const hasDevicesLimit = dashboardState.subInfo?.devices_count !== undefined && dashboardState.subInfo?.devices_count !== null;
  const devicesLimit = hasDevicesLimit ? getDevicesLimit(dashboardState.subInfo?.devices_count) : null;
  const devicesUsed = dashboardState.devicesTotal;
  const freeSlots = devicesLimit !== null ? getFreeDeviceSlots(devicesUsed, devicesLimit) : null;
  const displayServerTiles = useMemo(() => createServerTilesWithPing(serverTiles), []);
  const subscriptionEndDate = formatSubscriptionEndDate(dashboardState.subInfo?.end_date);
  const daysUntilEnd = getDaysUntilEnd(dashboardState.subInfo?.end_date);
  const isSubscriptionExpiringSoon = hasActiveSubscription && daysUntilEnd !== null && daysUntilEnd <= 3;
  const trialStatus = dashboardState.trialStatus;
  const isTrialUsed = trialStatus?.trial_used ?? dashboardState.userState?.trial_used;
  const isTelegramLinkedForTrial = trialStatus?.telegram_linked ?? isTelegramLinked;
  const trialCanActivate = Boolean(trialStatus?.trial_can_activate);
  const trialActivationStatus = trialStatus?.trial_activation_status;
  const shouldShowTrial = isTrialUsed === false || trialCanActivate || trialActivationStatus === 'telegram_required';
  const trialInstruction = trialStatus?.trial_activation_instruction || 'Чтобы активировать пробную подписку, привяжите Telegram к этому аккаунту.';
  const displayTelegramUsername = telegramUsername ? `@${telegramUsername.replace(/^@/, '')}` : '';
  const trialStatusLabel = getTrialStatusLabel(trialActivationStatus, trialCanActivate, isTelegramLinkedForTrial);
  const canShowTelegramConnect = !isTelegramLinkedForTrial && canConnectTelegram;

  const handleTrialActivate = async () => {
    if (isTrialActivating) {
      return;
    }

    setTrialError('');
    setIsTrialActivating(true);

    try {
      const nextState = await activateTrial();
      setDashboardState((currentState) => ({
        ...currentState,
        userState: {
          ...(currentState.userState || {}),
          ...(nextState || {}),
          trial_used: nextState?.trial_used ?? true,
        },
        trialStatus: {
          ...(currentState.trialStatus || {}),
          ...(nextState || {}),
          trial_used: nextState?.trial_used ?? true,
          trial_can_activate: false,
        },
      }));
      window.dispatchEvent(new Event('mifvpn:refresh'));
      setIsTrialSuccessOpen(true);
    } catch (error) {
      setTrialError(getTrialActivationError(error));
    } finally {
      setIsTrialActivating(false);
    }
  };

  return (
    <div className="cabinet-page">
      <h1>{hasActiveSubscription ? 'Ваш доступ готов к подключению' : 'Подписка не активна'}</h1>

      <section className="cabinet-hero-card">
        <div>
          <div className="subscription-title-row">
            <span className="cabinet-muted">Текущая подписка</span>
            <button className="subscription-refresh-button" type="button" aria-label="Обновить данные подписки" onClick={onRefreshDashboard} disabled={isLoading}>
              <span aria-hidden="true">↻</span>
            </button>
          </div>
          <h2>{getSubscriptionTitle(dashboardState)}</h2>
          {hasActiveSubscription && subscriptionEndDate ? (
            <div className={`subscription-date-chip${isSubscriptionExpiringSoon ? ' warning' : ''}`}>
              <span>Дата окончания подписки</span>
              <strong>{subscriptionEndDate}</strong>
            </div>
          ) : null}
          <p>{isLoading ? 'Загружаем данные аккаунта...' : hasActiveSubscription ? 'Подключайтесь без новой настройки: приложение уже синхронизировано и готово к запуску на телефоне и десктопе.' : 'Выберите тариф, чтобы получить быстрый доступ к серверам и импорту конфигов.'}</p>
          <div className="cabinet-actions">
            {hasActiveSubscription ? (
              isSubscriptionExpiringSoon ? (
                <>
                  <button className="button button-primary" type="button" onClick={() => onSelectView('shop')}>Продлить подписку</button>
                  <button className="button button-secondary" type="button" onClick={() => onSelectView('import')}>Подключиться</button>
                </>
              ) : (
                <>
                  <button className="button button-primary" type="button" onClick={() => onSelectView('import')}>Подключиться</button>
                  <button className="button button-secondary" type="button" onClick={() => onSelectView('shop')}>Продлить заранее</button>
                </>
              )
            ) : (
              <button className="button button-primary" type="button" onClick={() => onSelectView('shop')}>Купить подписку</button>
            )}
          </div>
        </div>
        <div className="cabinet-status-stack">
          <div><span>Статус</span><strong className={hasActiveSubscription ? 'green-text' : ''}>{hasActiveSubscription ? 'Активна' : 'Нет подписки'}</strong></div>
          {devicesLimit !== null ? <div><span>Устройств</span><strong>{devicesUsed} из {devicesLimit}</strong></div> : null}
        </div>
      </section>

      <div className="dashboard-grid">
        {shouldShowTrial ? (
          <section className="trial-panel">
            <h2>Получение пробной подписки</h2>
            <p>Пробная подписка действует 3 дня. Активировать ее можно после привязки Telegram.</p>
            <div className="trial-status-grid">
              <div>
                <span>Telegram</span>
                <strong className={isTelegramLinkedForTrial ? 'green-text' : ''}>
                  {isTelegramLinkedForTrial ? displayTelegramUsername || 'Привязан' : 'Не привязан'}
                </strong>
              </div>
              <div>
                <span>Пробная</span>
                <strong>{trialStatusLabel}</strong>
              </div>
            </div>
            {trialCanActivate ? (
              <button className="trial-activate-button" type="button" onClick={handleTrialActivate} disabled={isTrialActivating}>
                {isTrialActivating ? 'Активируем...' : 'Активировать пробную подписку'}
              </button>
            ) : (
              <div className="trial-instruction">
                <strong>Как активировать</strong>
                <p>{trialInstruction}</p>
                {canShowTelegramConnect ? (
                  <button className="trial-activate-button" type="button" onClick={onConnectTelegram} disabled={isTelegramConnecting}>
                    {isTelegramConnecting ? 'Связываем...' : 'Связать Telegram'}
                  </button>
                ) : (
                  <button className="trial-activate-button" type="button" onClick={() => openExternalLink(TELEGRAM_LINKS.miniApp)}>
                    Открыть Telegram
                  </button>
                )}
                {telegramStatus ? <span className="trial-telegram-status">{telegramStatus}</span> : null}
              </div>
            )}
            {trialError ? <p className="trial-error">{trialError}</p> : null}
          </section>
        ) : (
          <section className="cabinet-panel wallet-preview">
            <h2>Кошелек и рефералка</h2>
            <button className="dashboard-device-link wallet" type="button" onClick={() => onSelectView('wallet')}>
              <span className="dashboard-wallet-icon" aria-hidden="true" />
              <span>
                <strong>Бонусы и приглашения</strong>
                <small>Баланс, реферальная ссылка и история операций.</small>
              </span>
              <b>Открыть</b>
            </button>
          </section>
        )}

        <section className="cabinet-panel useful-panel">
          <h2>Полезное</h2>
          <button type="button" onClick={() => onSelectView('import')}><strong>Инструкция</strong><span>Установка и импорт конфига.</span></button>
          <button type="button" onClick={() => openExternalLink(TELEGRAM_LINKS.support)}><strong>Поддержка</strong><span>Поможем, если что-то не работает.</span></button>
          <button type="button" onClick={() => openExternalLink(TELEGRAM_LINKS.channel)}><strong>Канал</strong><span>Новости серверов и акций.</span></button>
          <button type="button" onClick={() => openExternalLink(TELEGRAM_LINKS.bot)}><strong>Бот</strong><span>Trial, профиль и быстрые действия.</span></button>
        </section>

        <section className="cabinet-panel devices-preview">
          <h2>Подключенные устройства</h2>
          <button className="dashboard-device-link" type="button" onClick={() => onSelectView('devices')}>
            <span className="dashboard-device-icon" aria-hidden="true" />
            <span>
              <strong>{devicesLimit !== null ? `${devicesUsed} из ${devicesLimit} устройств занято` : `${devicesUsed} устройств подключено`}</strong>
              {freeSlots !== null ? <small>{getDeviceSlotsText(freeSlots)}</small> : null}
            </span>
            <b>Открыть</b>
          </button>
        </section>

        <section className="cabinet-panel server-grid-panel">
          <h2>🟢 Статусы серверов:</h2>
          <div className="cabinet-server-grid">
            {displayServerTiles.map((server) => (
              <div className={`cabinet-server-tile ${server.available ? 'available' : 'coming'}`} key={server.code}>
                <span className="server-icon">{server.icon}</span>
                <strong>{server.code}</strong>
                <span>{server.title}</span>
                <small>{server.ping}</small>
              </div>
            ))}
          </div>
        </section>
      </div>

      {isTrialSuccessOpen ? (
        <div className="cabinet-modal-overlay trial-success-overlay" role="presentation" onMouseDown={() => setIsTrialSuccessOpen(false)}>
          <section
            className="cabinet-modal trial-success-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="trial-success-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button className="cabinet-modal-close" type="button" aria-label="Закрыть окно" onClick={() => setIsTrialSuccessOpen(false)}>x</button>
            <div className="trial-success-head">
              <span>Пробная подписка</span>
              <h2 id="trial-success-title">Пробная подписка активирована</h2>
              <p>Доступ открыт на 3 дня. Подключайтесь сейчас, а через 3 дня возвращайтесь, чтобы продлить подписку и сохранить доступ.</p>
            </div>
            <div className="trial-success-actions">
              <button className="button button-primary" type="button" onClick={() => {
                setIsTrialSuccessOpen(false);
                onSelectView('import');
              }}>
                Перейти к подключению
              </button>
              <button className="button button-secondary" type="button" onClick={() => setIsTrialSuccessOpen(false)}>
                Закрыть
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
